import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  ISBD_SUPPORT_EMAIL,
  ISBD_SUPPORT_WHATSAPP,
  NETWORK_EXPERT_CATEGORIES,
  communityPlatformIcon,
  communityPlatformLabel,
  mailtoLink,
  networkExpertInitials,
  whatsappLink,
  type CommunityChannel,
  type NetworkExpert,
} from '@isbd/shared';
import { invokeEdgeFunction, kuunda } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';
import { signStaffResourceUrl } from '@/lib/bunnyStorage';
import { AgendaEventsPanel } from '@/features/agenda/AgendaEventsPanel';
import './network.css';

const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#0d9488', '#4f46e5'];

function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i) * (i + 1)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h]!;
}

function ExpertAvatar({ expert }: { expert: NetworkExpert }) {
  const [src, setSrc] = useState<string | null>(expert.avatar_url);
  useEffect(() => {
    let cancelled = false;
    if (expert.avatar_url) {
      setSrc(expert.avatar_url);
      return;
    }
    if (!expert.avatar_storage_path) {
      setSrc(null);
      return;
    }
    void signStaffResourceUrl({ storagePath: expert.avatar_storage_path })
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
    };
  }, [expert.avatar_url, expert.avatar_storage_path]);

  return (
    <div className="nk-avatar" style={{ background: avatarColor(expert.full_name) }}>
      {src ? <img src={src} alt="" /> : networkExpertInitials(expert.full_name)}
      {expert.is_online ? <span className="nk-online" title="En ligne" /> : null}
    </div>
  );
}

function ExpertProfileModal({
  expert,
  onClose,
  onMessage,
}: {
  expert: NetworkExpert;
  onClose: () => void;
  onMessage: () => void;
}) {
  return (
    <div className="nk-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="nk-modal"
        role="dialog"
        aria-modal
        aria-labelledby="nk-profile-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
          <ExpertAvatar expert={expert} />
          <div>
            <h3 id="nk-profile-title">{expert.full_name}</h3>
            <p className="muted" style={{ margin: 0 }}>
              {expert.role_title}
              {expert.handle ? ` · @${expert.handle.replace(/^@/, '')}` : ''}
            </p>
          </div>
        </div>
        {expert.bio ? <p style={{ marginTop: '1rem' }}>{expert.bio}</p> : null}
        {expert.specialties?.length ? (
          <div className="nk-tags" style={{ marginTop: '0.75rem' }}>
            {expert.specialties.map((s) => (
              <span key={s} className="nk-tag">
                {s}
              </span>
            ))}
          </div>
        ) : null}
        <div className="nk-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Fermer
          </button>
          <button type="button" className="btn btn-primary" onClick={onMessage}>
            Envoyer un message
          </button>
        </div>
      </div>
    </div>
  );
}

function ExpertMessageModal({
  expert,
  onClose,
}: {
  expert: NetworkExpert;
  onClose: () => void;
}) {
  const { profile } = useAuth();
  const toast = useToast();
  const [subject, setSubject] = useState('Sollicitation expert ISBD');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!profile) return;
    setLoading(true);
    try {
      const text = body.trim();
      if (!text) {
        toast.error('Écrivez votre message.');
        return;
      }

      const senderName =
        [profile.first_name, profile.last_name].filter(Boolean).join(' ') || profile.email || 'Abonné ISBD';
      const composed = `${text}\n\n— ${senderName}${profile.email ? ` (${profile.email})` : ''}`;

      try {
        const result = await invokeEdgeFunction<{
          emailSent?: boolean;
          whatsappSent?: boolean;
          mailtoUrl?: string | null;
          whatsappUrl?: string | null;
          message?: string;
        }>('network-notify-expert', {
          expert_id: expert.id,
          subject: subject.trim(),
          body: text,
        });

        if (result.mailtoUrl) window.open(result.mailtoUrl, '_blank');
        if (result.whatsappUrl) {
          window.open(result.whatsappUrl, '_blank', 'noopener,noreferrer');
        }

        toast.success(
          result.emailSent
            ? 'E-mail envoyé à support@isbd360.com. Ouverture WhatsApp…'
            : result.message || 'Message enregistré. Ouverture des canaux…',
        );
      } catch {
        // Fallback local : e-mail support + WhatsApp fixe
        const mailBody =
          `Sollicitation pour l’expert : ${expert.full_name}\n` +
          `Sujet : ${subject.trim()}\n\n` +
          `${composed}`;
        const { error: err } = await kuunda.from('network_expert_messages').insert({
          expert_id: expert.id,
          sender_id: profile.id,
          subject: subject.trim(),
          body: text,
          channel: 'both',
        });
        if (err) throw err;
        window.open(
          mailtoLink(ISBD_SUPPORT_EMAIL, `[ISBD] ${subject.trim()} — ${expert.full_name}`, mailBody),
          '_blank',
        );
        window.open(
          whatsappLink(ISBD_SUPPORT_WHATSAPP, `Sollicitation expert ${expert.full_name}\n${subject.trim()}\n\n${composed}`),
          '_blank',
          'noopener,noreferrer',
        );
        toast.success('Message enregistré. Ouverture e-mail et WhatsApp…');
      }
      setTimeout(onClose, 1100);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Envoi impossible');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="nk-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="nk-modal"
        role="dialog"
        aria-modal
        aria-labelledby="nk-msg-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="nk-msg-title">Message à {expert.full_name}</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Le message est transmis à support@isbd360.com et via WhatsApp.
        </p>
        <form onSubmit={(e) => void submit(e)}>
          <div className="field">
            <label>Sujet</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} required />
          </div>
          <div className="field">
            <label>Message</label>
            <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} required />
          </div>
          <div className="nk-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Envoi…' : 'Envoyer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function NetworkPage() {
  const toast = useToast();
  const [experts, setExperts] = useState<NetworkExpert[]>([]);
  const [communities, setCommunities] = useState<CommunityChannel[]>([]);
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [profileExpert, setProfileExpert] = useState<NetworkExpert | null>(null);
  const [messageExpert, setMessageExpert] = useState<NetworkExpert | null>(null);

  useEffect(() => {
    void (async () => {
      const [e, c] = await Promise.all([
        kuunda
          .from('network_experts')
          .select(
            'id, full_name, role_title, handle, bio, category, specialties, email, whatsapp, avatar_url, avatar_storage_path, is_online, is_published, sort_order',
          )
          .eq('is_published', true)
          .order('sort_order', { ascending: true })
          .order('full_name', { ascending: true }),
        kuunda
          .from('community_channels')
          .select('id, platform, label, url, description, sort_order, is_active, image_url, image_storage_path, created_at')
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
      ]);
      if (e.error) toast.error(e.error.message);
      else setExperts((e.data ?? []) as NetworkExpert[]);
      if (c.error) toast.error(c.error.message);
      else setCommunities((c.data ?? []) as CommunityChannel[]);
    })();
  }, [toast]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return experts.filter((ex) => {
      if (category !== 'all' && ex.category !== category) return false;
      if (!q) return true;
      const hay = [ex.full_name, ex.role_title, ex.handle, ...(ex.specialties ?? [])].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [experts, category, query]);

  return (
    <div className="u-page nk-page">
      <div className="u-hero nk-hero">
        <div>
          <p className="u-subtitle">Collaborez avec des experts et accédez aux ressources de la communauté.</p>
        </div>
      </div>

      <div className="nk-layout">
        <section className="nk-panel">
          <div className="nk-panel-head">
            <h3>Annuaire des experts</h3>
            <label className="nk-search">
              <span aria-hidden>🔍</span>
              <input
                type="search"
                placeholder="Trouver un expert..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </label>
          </div>

          <div className="nk-filters" role="tablist" aria-label="Spécialités">
            {NETWORK_EXPERT_CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                role="tab"
                aria-selected={category === c.key}
                className={`nk-pill${category === c.key ? ' is-active' : ''}`}
                onClick={() => setCategory(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="u-empty">Aucun expert publié pour le moment.</p>
          ) : (
            <ul className="nk-expert-list">
              {filtered.map((ex) => (
                <li key={ex.id} className="nk-expert">
                  <ExpertAvatar expert={ex} />
                  <div className="nk-expert-meta">
                    <strong>{ex.full_name}</strong>
                    <p className="nk-role">{ex.role_title || 'Expert'}</p>
                    {ex.handle ? <p className="nk-handle">@{ex.handle.replace(/^@/, '')}</p> : null}
                    <div className="nk-tags">
                      {(ex.specialties ?? []).slice(0, 4).map((s) => (
                        <span key={s} className="nk-tag">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="nk-expert-actions">
                    <button type="button" className="btn btn-outline" onClick={() => setProfileExpert(ex)}>
                      Profil
                    </button>
                    <button type="button" className="btn btn-msg" onClick={() => setMessageExpert(ex)}>
                      Message
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <aside className="nk-side">
          <section className="nk-resources">
            <h3>
              <span aria-hidden>🚀</span> Centre de Ressources
            </h3>
            <div className="nk-res-grid">
              <Link to="/app/reseau/guides" className="nk-res-card">
                <span className="nk-res-icon" aria-hidden>
                  📘
                </span>
                Guides Pratiques
              </Link>
              <Link to="/app/reseau/masterclass" className="nk-res-card">
                <span className="nk-res-icon" aria-hidden>
                  ▶
                </span>
                Masterclass Vidéo
              </Link>
            </div>
          </section>

          <section className="nk-panel">
            <h3 style={{ marginTop: 0 }}>Communauté</h3>
            <p className="muted" style={{ marginTop: 0, marginBottom: '0.65rem', fontSize: '0.88rem' }}>
              Rejoignez WhatsApp, Facebook, TikTok, Telegram, LinkedIn…
            </p>
            <ul className="nk-community-list">
              <li>
                <Link to="/app/reseau/communautes" className="nk-community-item">
                  <span className="nk-community-icon is-forum" aria-hidden>
                    💬
                  </span>
                  <div>
                    <strong>Forum d&apos;entraide</strong>
                    <span>
                      {communities.length > 0
                        ? `${communities.length} communauté${communities.length > 1 ? 's' : ''} à rejoindre`
                        : 'Voir toutes les communautés'}
                    </span>
                  </div>
                  <span className="nk-chevron" aria-hidden>
                    ›
                  </span>
                </Link>
              </li>
              {communities.slice(0, 5).map((ch) => (
                <li key={ch.id}>
                  <a
                    href={ch.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nk-community-item"
                    title={`Rejoindre ${communityPlatformLabel(ch.platform)}`}
                  >
                    <span className="nk-community-icon is-events" aria-hidden>
                      {communityPlatformIcon(ch.platform)}
                    </span>
                    <div>
                      <strong>{ch.label}</strong>
                      <span>
                        {communityPlatformLabel(ch.platform)}
                        {ch.description ? ` · ${ch.description}` : ' · Cliquez pour rejoindre'}
                      </span>
                    </div>
                    <span className="nk-chevron" aria-hidden>
                      ›
                    </span>
                  </a>
                </li>
              ))}
              {communities.length > 5 ? (
                <li>
                  <Link to="/app/reseau/communautes" className="nk-community-item">
                    <span className="nk-community-icon is-forum" aria-hidden>
                      ＋
                    </span>
                    <div>
                      <strong>Voir toutes les communautés</strong>
                      <span>+{communities.length - 5} autres plateformes</span>
                    </div>
                    <span className="nk-chevron" aria-hidden>
                      ›
                    </span>
                  </Link>
                </li>
              ) : null}
            </ul>
          </section>

          <AgendaEventsPanel />
        </aside>
      </div>

      {profileExpert ? (
        <ExpertProfileModal
          expert={profileExpert}
          onClose={() => setProfileExpert(null)}
          onMessage={() => {
            setMessageExpert(profileExpert);
            setProfileExpert(null);
          }}
        />
      ) : null}
      {messageExpert ? <ExpertMessageModal expert={messageExpert} onClose={() => setMessageExpert(null)} /> : null}
    </div>
  );
}
